// =================================================================================================
// BROKEN WORKFLOW EXPLAINED
// =================================================================================================
//
// The reset-password flow (module/auth-fence/src/web/auth/reset_password.ts) was failing in Firefox
// while working in Chrome. Root cause: the previous version of this file queried descendant custom
// elements inside its constructor, which is unsafe during a `cloneNode(true)` walk.
//
// PAGE LOAD (works — templates get upgraded cleanly)
// --------------------------------------------------
//   - <body> contains <bux-view-templates><bux-view name="view3">…<bux-component-password-creator>
//     …</bux-component-password-creator>…</bux-view></bux-view-templates>.
//     Server-rendered via password_creator.rs / password_creator.pmd.
//   - Module scripts run in order: password.mts defines `bux-input-text-password` first;
//     password_creator.mts then defines `bux-component-password-creator`. Each `define` walks the
//     document and upgrades matching elements in tree order. Because the inner type is defined
//     first, when the outer element's constructor runs on the template node, its descendants are
//     already `BuxInputTextPassword` instances. The old `SEC(...)` calls in the constructor worked.
//     No Firefox/Chrome divergence at this point.
//
// USER REACHES PASSWORD STEP (this is where Firefox broke)
// --------------------------------------------------------
//   reset_password.ts:100-109 —
//     class EnterPasswordState {
//         $view = copy_view("view3");                                              // (1)
//         $form = SEC(HTMLFormElement, this.$view, "form");
//         $password_creator = SEC(BuxComponentPasswordCreator, this.$view,
//                                 "bux-component-password-creator");
//         …
//     }
//
//   Line (1) calls copy_view("view3"), which is literally $template.cloneNode(true) — see
//   lib/bux/src/document/mod.mts:21. The cloned subtree is DETACHED, and cloning a custom element
//   runs its constructor via the "create an element" algorithm with *synchronous custom elements
//   = true*. Per the HTML spec, clone walks the tree node-by-node: for each cloned
//   `bux-component-password-creator`, its constructor fires BEFORE its children have been cloned
//   into it.
//
//   Inside that constructor (old code):
//     1. `globalThis.customElements.upgrade(this)` — a spec no-op here; `this` is already defined
//        and, critically, has no children yet.
//     2. `SEC(BuxInputTextPassword, this, "bux-input-text-password[name=password]")` — runs
//        querySelector against `this`, which has no children, so querySelector returns null and
//        SEC throws "Element not found".
//
//   So the error isn't Firefox-specific in principle — it's an ordering issue that's always
//   present in clone paths. What the two engines differ on historically is *when exactly* they
//   attach cloned children relative to the custom-element constructor. Chrome's implementation
//   effectively had children attached by the time querySelector ran; Firefox follows the spec
//   step order strictly (create parent → create & append children) so querySelector finds nothing.
//
// WHY THE FIX IS CORRECT FOR THIS FLOW
// ------------------------------------
//   The move to connectedCallback works even though copy_view returns a detached clone. clone
//   produces unresolved-children mid-construction, but the full view is later inserted into the
//   document via $views.replaceChildren(this.$view) at reset_password.ts:120. Insertion into a
//   connected tree runs pending upgrade reactions in tree order, and connectedCallback on the
//   password creator fires AFTER its descendants are upgraded. SEC then sees real instances.
//
//   Bonus observation: the class-field initializer order in EnterPasswordState is also fragile —
//   `$password_creator = SEC(...)` runs BEFORE `$views.replaceChildren(this.$view)`, so it runs
//   against the detached clone. With the current code that's fine because SEC only asserts class
//   identity, and by that point cloneNode has finished so children are attached to the outer
//   clone (just not to the document). In the OLD code, SEC(BuxComponentPasswordCreator, …) would
//   already have crashed during the clone walk itself, before EnterPasswordState's constructor
//   body even got to that line.
//
// HOW TO CONFIRM THE FLOW IN A BROWSER
// ------------------------------------
//   Add at the top of connectedCallback:
//     console.log("[bux-component-password-creator] connected",
//         { childCount: this.childElementCount, stack: new Error().stack });
//   Run the reset-password flow in Firefox. In the old code you'd see SEC throw with stack
//   pointing at EnterPasswordState class-field init → copy_view → cloneNode. In the new code
//   you'll see connect fire after the view is injected via $views.replaceChildren(this.$view).
// =================================================================================================

import "./password_creator.mcss";
import "@crate/input/text/password.mts";
import { SEC } from "@arlib/lib.mts";
import { password_analysis } from "@arlib/password.mts";
import { BuxInputTextPassword } from "@crate/input/text/password.mts";

class BuxComponentPasswordCreator extends HTMLElement {
    // Child refs are assigned in connectedCallback, NOT the constructor.
    //
    // Why not the constructor?
    //   When a custom element's constructor runs, its custom-element descendants may
    //   not yet be upgraded, so `instanceof BuxInputTextPassword` would be false. This
    //   happens in several legitimate paths:
    //     1. The HTML parser reaches our start tag before our children are parsed.
    //     2. `document.createElement("bux-component-password-creator")` runs before
    //        any children are appended.
    //     3. Our definition is registered before a descendant's definition, leaving
    //        descendants as inert HTMLElements when our constructor runs.
    //   `customElements.upgrade(this)` does NOT rescue us: per spec it only *enqueues*
    //   upgrade reactions on the current custom-element-reactions stack frame, and
    //   those reactions don't run until our constructor returns. Firefox follows the
    //   spec strictly; Chrome happens to drain the queue synchronously — which is why
    //   the old code worked in Chrome and broke in Firefox.
    //
    // Why connectedCallback is bullet-proof:
    //   By the time connectedCallback fires, the element is in a connected tree and
    //   every pending upgrade reaction for the subtree has already run in tree order.
    //   Every descendant custom element is a fully upgraded instance of its class.
    //   This holds uniformly across parser-driven, define-driven, and programmatic
    //   construction in every conformant engine.
    //
    // `declare` gives us non-nullable field types without `!:` or `| undefined`. It
    // emits no initializer, so the property is genuinely absent until connectedCallback
    // assigns it — letting `if (!this.$password)` serve as a clean first-connect guard.
    declare private $password: BuxInputTextPassword;
    declare private $confirm: BuxInputTextPassword;
    declare private $meter: HTMLMeterElement;
    declare private $status: HTMLOutputElement;

    // Stable handler refs (arrow fields keep `this` bound). Identities MUST be stable
    // so removeEventListener in disconnectedCallback matches the add in connectedCallback;
    // a fresh `.bind(this)` in each callback would produce a new function that
    // removeEventListener can't find, leaking handlers across connect/disconnect cycles.
    private on_password_input = () => this.refresh();
    private on_confirm_input = () => this.refresh();
    private on_confirm_change = () => this.validate();

    connectedCallback() {
        // connectedCallback can fire more than once (remove → reinsert). First-connect
        // does the SEC lookups and initial refresh; later reconnects reuse the refs.
        if (!this.$password) {
            this.$password = SEC(BuxInputTextPassword, this, "bux-input-text-password[name=password]");
            this.$confirm = SEC(BuxInputTextPassword, this, "bux-input-text-password[name=confirm_password]");
            this.$meter = SEC(HTMLMeterElement, this, "meter");
            this.$status = SEC(HTMLOutputElement, this, "output");
            this.refresh();
        }

        this.$password.addEventListener("input", this.on_password_input);
        this.$confirm.addEventListener("input", this.on_confirm_input);
        this.$confirm.addEventListener("change", this.on_confirm_change);
    }

    disconnectedCallback() {
        this.$password.removeEventListener("input", this.on_password_input);
        this.$confirm.removeEventListener("input", this.on_confirm_input);
        this.$confirm.removeEventListener("change", this.on_confirm_change);
    }

    // Update the meter and status
    private refresh() {
        const analysis = password_analysis(this.value ?? "");
        this.$meter.value = analysis.meter_value;
        this.$status.textContent = analysis.meter_text;

        // Clear any previous errors when user types
        this.$confirm.set_e(undefined);
    }

    // Public API
    get value(): string | undefined {
        return this.$password.value || undefined;
    }

    set_e(value: string | undefined) {
        this.$password.set_e(value);
    }

    set_confirm_e(value: string | undefined) {
        this.$confirm.set_e(value);
    }

    public get_if_valid(): string | undefined {
        if (this.validate()) {
            return this.value;
        }
        return undefined;
    }

    // Validate passwords match and show error if they don't
    public validate(): boolean {
        const p1 = this.$password.value;
        const p2 = this.$confirm.value;

        this.$password.set_e(undefined);
        this.$confirm.set_e(undefined);

        if (p1 && p2) {
            const analysis = password_analysis(p1);
            if (!analysis.acceptable) {
                this.set_e("Password does not meet strength requirements");
                return false;
            }

            if (p1 !== p2) {
                this.set_confirm_e("Passwords do not match");
                return false;
            }

            return true;
        }

        if (!p1) {
            this.set_e("Password is required");
        }

        if (!p2) {
            this.set_confirm_e("Please confirm your password");
        }

        return false;
    }
}

customElements.define("bux-component-password-creator", BuxComponentPasswordCreator);
export default BuxComponentPasswordCreator;
